import React, { useContext, useState, useMemo, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import { 
  Paper,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  SvgIcon
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import HighlightIcon from '@material-ui/icons/Highlight';
import { viewObjects } from 'store/explore/views';
import PropertyDataDetail from "components/explore/views/objectDetail/PropertyDataDetail";
import MarketsDetail from 'components/explore/views/objectDetail/MarketsDetail';
import CustomObjectDetail from 'components/explore/views/objectDetail/CustomObjectDetail';
import { setExploreSelectedObjectId, deleteExploreViewObject, updateExploreObjectById, setConfirmationModalState,
  setExploreActiveEditingObject, setExploreActiveDrawingObject, highlightExploreViewObject, setIsSideBarOpen } from 'store/actions';
import { Constants } from 'utilities/constants';
import NameInput from 'components/explore/views/NameInput';
import { sendClientLog } from 'services/logService';
import { extendBoundsToObject } from 'components/explore/gmap/helpers';
import { MapRefContext } from 'components/explore/Explore';
import CloseIcon from '@material-ui/icons/Close';
import { fitBoundsWithZoom } from 'components/explore/gmap/helpers';

const ViewObjectDetail = ({
  classes,
  selectedView,
  selectedObjectId,
  setExploreSelectedObjectId,
  deleteExploreViewObject,
  updateExploreObjectById,
  setConfirmationModalState,
  activeEditingObject,
  activeDrawingObject,
  setExploreActiveEditingObject,
  setExploreActiveDrawingObject,
  highlightExploreViewObject,
  setIsSideBarOpen,
  isMobile,
}) => {
  const mapRef = useContext(MapRefContext);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);

  const selectedObject = useMemo(() => {
    if (!selectedView || selectedObjectId == null) return null;
    return selectedView.objects.find(object => object.id === selectedObjectId);
  }, [selectedView, selectedObjectId]);

  useEffect(() => {
    if (selectedObject && !selectedObject.visible) {
      handleBackToView(false); // False to disallow confirmation popup
    }
  }, [selectedObject]);

  if (!selectedObject) return null;

  const openMoreMenu = (e) => {
    setMoreMenuAnchorEl(e.currentTarget);
  }

  const closeMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  }

  const handleEditingCancel = () => {
    setExploreActiveEditingObject({ id: selectedObject.id, type: selectedObject.type, drawingMode: viewObjects[selectedObject.type].drawingMode, isCancelling: true });
  }

  const handleDrawingCancel = () => {
    setExploreActiveDrawingObject(null);
  }

  const getDetailComponent = (type) => { // Define which object type-specific detail page should be mounted. Detail page will contain editors and other details.
    switch (selectedObject.type) {
      case Constants.ViewsObjectType.PROPERTYDATA:
        return <PropertyDataDetail object={selectedObject} />
      case Constants.ViewsObjectType.MARKETS:
        return <MarketsDetail object={selectedObject} />
      default:
        return <CustomObjectDetail object={selectedObject} handleEditingCancel={handleEditingCancel} />
    }
  }

  const handleDeleteObject = () => {
    closeMoreMenu();
    const deleteActions = () => {
      deleteExploreViewObject(selectedObject.layerId, selectedObject.id);
      if (activeEditingObject) {
        setExploreActiveEditingObject(null);
      }
      if (activeDrawingObject) {
        handleDrawingCancel(null);
      }
    }

    if (selectedObject.needsPositionSet) {
      deleteActions();
      sendClientLog('Info',`Explore Views - User deleted object from ViewObjectDetail menu for object id ${selectedObject.id}, layer id ${selectedObject.layerId}, view id ${selectedView.id}`);
    } else {
      setConfirmationModalState({
        isModalOpen: true,
        handleConfirmation: () => {
          closeConfirmationModal();
          deleteActions();
          sendClientLog('Info',`Explore Views - User clicked and confirmed Delete on ViewObjectDetail menu for object id ${selectedObject.id}, layer id ${selectedObject.layerId}, view id ${selectedView.id}`);
        },
        handleCancellation: () => {
          closeConfirmationModal();
        },
        titleText: 'Confirm',
        contentText: `Are you sure you want to delete object "${selectedObject.description}"?`
      });
    }
  }
  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  }

  // const handleDescriptionChange = e => {
  //   const { value } = e.target;
  //   if (value === selectedObject.description) return;
  //   updateExploreObjectById(selectedObjectId, { description: value });
  // }

  const handleBackToView = (allowConfirmation = true) => {
    let shouldOpenConfirmation = false;
    if (activeEditingObject && !activeEditingObject.isSaving) {
      if (allowConfirmation) {
        shouldOpenConfirmation = true;
      } else {
        handleEditingCancel();
      }
    }
    if (activeDrawingObject) {
      if (allowConfirmation) {
        shouldOpenConfirmation = true;
      } else {
        handleDrawingCancel();
      }
    }
    if (shouldOpenConfirmation) {
      setConfirmationModalState({
        isModalOpen: true,
        handleConfirmation: () => {
          closeConfirmationModal();
          // setExploreSelectedObjectId(null);
        },
        handleCancellation: () => {
          closeConfirmationModal();
        },
        titleText: 'Unfinished changes',
        contentText: `Changes to ${selectedObject.description} are currently pending. Please finish before returning to the View.`,
        showCancel: false,
        confirmButtonText: 'OK'
      })
    } else {
      setExploreSelectedObjectId(null);
    }
  }

  const handleBestFitObject = () => {
    closeMoreMenu();
    const bounds = new window.google.maps.LatLngBounds();
    const expandedBounds = extendBoundsToObject(bounds, selectedObject, mapRef);
    if (mapRef?.current) {
      const zoom = mapRef.current.getZoom();
      fitBoundsWithZoom(mapRef.current, expandedBounds);
      if (selectedObject.type === Constants.ViewsObjectType.LABEL) { // Don't use new zoom for labels
        mapRef.current.setZoom(zoom); // Set zoom to previous value
      }
    }
    sendClientLog('Info',`Explore Views - User used best fit from ViewObjectDetail for object id ${selectedObjectId}, layer id ${selectedObject.layerId}`);
  }

  const handleRenameClick = (timeout = 0) => {
    closeMoreMenu();
    setTimeout(() => setIsEditingName(true), timeout);
  }

  const handleNameSave = name => {
    setIsEditingName(false);
    if (name === selectedObject.description) return;
    updateExploreObjectById(selectedObjectId, { description: name });
  }

  const handleHighlightObject = () => {
    highlightExploreViewObject(selectedObject.id, selectedObject.type);
    sendClientLog('Info',`Explore Views - User clicked Highlight from ViewObjectDetail for object id ${selectedObjectId}, layer id ${selectedObject.layerId}`);
  }

  const isObjectHighlightDisabled = (selectedObject.type === Constants.ViewsObjectType.PROPERTYDATA && !selectedObject.isFilterSet) || selectedObject.needsPositionSet;

  return (
    <Paper square elevation={0} className={isMobile ? classes.mobileContainer : classes.container}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Tooltip title='Back to View'>
            <IconButton
              onClick={e => handleBackToView()}
              size='small'
              color='inherit'
              classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
              style={{ margin: '0 4px'}}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={viewObjects[selectedObject.type]?.displayType ?? selectedObject.type}>
            <SvgIcon style={{ marginRight: 8 }}>
              <path d={viewObjects[selectedObject.type].icon} />
            </SvgIcon>
          </Tooltip>
          {isEditingName
              ? <NameInput name={selectedObject.description} isViewName invertColor onSave={handleNameSave} charLimit={Constants.ViewsCharLimit.OBJECTDESCRIPTION} />
              : <Typography variant='subtitle2' className={classes.headerText} color='inherit' onDoubleClick={handleRenameClick}>{selectedObject.description}</Typography>
            }
          {/* <Typography variant='subtitle2' className={classes.headerText} color='inherit'>{viewObjects[selectedObject.type]?.displayType ?? selectedObject.type}</Typography> */}
        </div>
        <div className={classes.headerRight}>
          <Tooltip title='Highlight on map'>
            <span>
              <IconButton
                onClick={handleHighlightObject}
                color='inherit'
                classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                disabled={isObjectHighlightDisabled}
              >
                <HighlightIcon />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            onClick={openMoreMenu}
            color='inherit'
            classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
          >
            <MoreHorizIcon />
          </IconButton>
          <Menu
            anchorEl={moreMenuAnchorEl}
            open={Boolean(moreMenuAnchorEl)}
            onClose={closeMoreMenu}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'bottom'
            }}
          >
            {selectedObject.type !== Constants.ViewsObjectType.PROPERTYDATA && selectedObject.type !== Constants.ViewsObjectType.MARKETS &&
              <MenuItem onClick={handleBestFitObject} disabled={!selectedObject.visible || selectedObject.needsPositionSet}>Fit Object on map</MenuItem>
            }
            <MenuItem onClick={() => handleRenameClick(100)}>Rename Object</MenuItem>
            <MenuItem onClick={handleDeleteObject}>Delete Object</MenuItem>
          </Menu>
          {isMobile && (
            <Tooltip title='Close Panel'>
              <span>
                <IconButton
                  onClick={() => setIsSideBarOpen(false)}
                  size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}>
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      <div className={classes.detailContainer}>
        {getDetailComponent()}
      </div>
    </Paper>
  )
}

const styles = theme => ({
  container: {
    zIndex: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  mobileContainer: { 
    zIndex: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'unset'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#3e4350',
    color: '#fff',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    margin: '8px 0',
    minWidth: 0
  },
  headerText: {
    fontSize: '1.2rem',
    // marginRight: 8,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  headerRight: {
    //color: '#fff'
    marginRight: 4,
    display: 'flex'
  },
  iconButton: {
    padding: '5px',
  },
  button: {
    marginTop: 8,
    // marginLeft: 24
  },
  editorContainer: {
    padding: '8px 24px 16px'
  },
  editorTitle: {
    marginLeft: -8
  },
  detailContainer: {
    overflow: 'auto',
    height: '100%'
  }
});

const mapStateToProps = ({ exploreViews, appState }) => {
  return {
    selectedObjectId: exploreViews.selectedObjectId,
    activeEditingObject: exploreViews.activeEditingObject,
    activeDrawingObject: exploreViews.activeDrawingObject,
    isMobile: appState.isMobile,
  }
};

const mapDispatchToProps = {
  setExploreSelectedObjectId,
  deleteExploreViewObject,
  updateExploreObjectById,
  setConfirmationModalState,
  setExploreActiveEditingObject,
  setExploreActiveDrawingObject,
  highlightExploreViewObject,
  setIsSideBarOpen
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ViewObjectDetail);
